import React, { useState, useEffect } from 'react';
import '../view-archer-styles.css';

// material-ui: theme, colors, icons
import { Avatar, Button, Divider, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, IconButton, MenuItem, InputLabel, Paper, Select, Slide, Stack, TextField, Typography } from '@mui/material/';
import { TransitionProps } from '@mui/material/transitions';
import { blue} from '@mui/material/colors';
import { Attachment, Close, RotateRight } from '@mui/icons-material/';

//components import
import AlertInfoDialog from '../../../components/elements/AlertInfoDialog';
import AlertInfo from '../../../dataObjects/AlertInfo';

interface UploadedFileViewerProps {
    fileUrl: string,
    responseId: number,
    isFileViewerDialogOpen: boolean,
    closeFileViewerDialog: () => void
}
const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="left" ref={ref} {...props} />;
});

const UploadedFileViewer = (props: UploadedFileViewerProps) => {

    //initial state setup
    const [fileUrl, setFileUrl] = useState(props.fileUrl);
    const [fileExtension, setFileExtension] = useState("");
    const [fileSize, setFileSize] = useState(100);
    const [timeStampSize, setTimeStampSize] = useState(24);
    const [iframeCount, setIframeCount] = useState(0);
    const [timeStampTime, setTimeStampTime] = useState(new Date().toLocaleString('en-US', { hour12: true }))

    useEffect(() => {
        setFileUrl(props.fileUrl);
        setOriginalFileExtension();
        setTimeStampTime(new Date().toLocaleString('en-US', { hour12: true }));
    }, [props.isFileViewerDialogOpen])

    const setOriginalFileExtension = () => {
        if (props.fileUrl) {
            let fileName = props.fileUrl.split('=')[1];
            let extension = fileName.split('.').pop();
            if (extension) {
                setFileExtension(extension);
            }
        }
    }

    const closeFileViewerDialog = () => {
        props.closeFileViewerDialog();
    }

    const resetIframe = () => {
        setIframeCount(iframeCount + 1);
    }

    const rotateUploadedFile = () => {

        let fileName = fileUrl.split('=')[1];

        let url = `/Archer3/RotateUploadedFile?fileName=${fileName}`
        fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: RotateUploadedFile',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    resetIframe();
                }
            }
            )
    }

    const resizeUploadedFile = (event:any) => {
        let fileSize = event.target.value;
        setFileSize(fileSize);
        let fileName = fileUrl.split('=')[1];
        let url = `/Archer3/ResizeUploadedFile?fileName=${fileName}&size=${fileSize}`
        fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: ResizeUploadedFile',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    resetIframe();
                }
            }
            )
    }

    const convertToPdf = () => {
        let fileName = fileUrl.split('=')[1];

        if (fileExtension.toLowerCase() === 'pdf') {
            let info = {
                type: 'error',
                title: 'Error: ConvertToPdf',
                errorMessage: 'File is already in PDF format',
                secondaryErrorMessage: ''
            };
            setAlertInfo(info);
            setAlertInfoDialog(true);
        }
        else { 
            let url = `/Archer3/ConvertToPdf?fileName=${fileName}`
            fetch(url)
                .then(result => result.json())
                .then(result => {
                    if (result.isError) {
                        let info = {
                            type: 'error',
                            title: 'Error: ConvertToPdf',
                            errorMessage: result.errorMessage,
                            secondaryErrorMessage: result.secondaryErrorMessage
                        };
                        setAlertInfo(info);
                        setAlertInfoDialog(true);
                    }
                    else {
                        //replace the link, and display PDF
                        let newPdfName = fileName.replace(/\.[^/.]+$/, "") + '_unstamped.pdf';
                        setFileUrl('/Archer3/uploads?fileName=' + newPdfName);
                        resetIframe();
                    }
                }
            )
        } 
    }

    const updateTimeStampSize = (event: any) => {
        setTimeStampSize(event.target.value);
    }

    const addTimeStamp = () => {

        let fileName = fileUrl.split('=')[1].toLowerCase();
        let isPDF = fileName.split('.').pop() === 'pdf' ? true : false;
        if (isPDF) {
            let url = `/Archer3/AddWatermark?fileName=${fileName}&timeStampTime=${timeStampTime}&stampSize=${timeStampSize}`
            fetch(url)
                .then(result => result.json())
                .then(result => {
                    if (result.isError) {
                        let info = {
                            type: 'error',
                            title: 'Error: AddWatermark',
                            errorMessage: result.errorMessage,
                            secondaryErrorMessage: result.secondaryErrorMessage
                        };
                        setAlertInfo(info);
                        setAlertInfoDialog(true);
                    }
                    else {
                        //replace the link, and display PDF with water mark
                        let newPdfName = fileName.replace("_unstamped.pdf", ".pdf");
                        setFileUrl('/Archer3/uploads?fileName=' + newPdfName);
                        resetIframe();
                    }
                })
        }
        else {
            let info = {
                type: 'error',
                title: 'Error: format error',
                errorMessage: 'Please convert the file to PDF first!',
                secondaryErrorMessage: ''
            };
            setAlertInfo(info);
            setAlertInfoDialog(true);
        }
    }

    const removeTimeStamp = () => {
        let fileName = fileUrl.split('=')[1];
        let unstampedPdfName = fileName.replace(".pdf", "_unstamped.pdf");
        setFileUrl('/Archer3/uploads?fileName=' + unstampedPdfName);
        resetIframe();
    }

    const revertToOriginal = () => {
        //original file url
        let fileName = props.fileUrl.split('=')[1];
        let url = `/Archer3/RevertToOriginal?fileName=${fileName}`
        fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: RevertToOriginal',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    setFileUrl(props.fileUrl);
                    resetIframe();
                }
            }
        )
    }

    const saveProcessedFile = () => {
        let fileName = fileUrl.split('=')[1];
        var params = [
            `responseId=${props.responseId}`,
            `fileName=${fileName}`,
            `originalExtension=${fileExtension}`
        ].join('&');
        let url = `/Archer3/saveProcessedPdf?${params}`
        fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: SaveProcessedPdf',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    props.closeFileViewerDialog();
                }
            }
            )
    }

    /*Error Handling*/
    const [isAlertInfoDialogOpen, setAlertInfoDialog] = useState(false);
    const [alertInfo, setAlertInfo] = useState<AlertInfo>();
    const closeAlertInfoDialog = () => {
        setAlertInfoDialog(false);
    }

    return (
        <>
            <Dialog
                open={props.isFileViewerDialogOpen}
                scroll={'paper'}
                fullWidth={true}
                maxWidth={'md'}
                TransitionComponent={Transition}
                keepMounted
                onClose={closeFileViewerDialog}
                sx={{
                    "& .MuiDialog-container": {
                        justifyContent: "flex-end",
                        alignItems: "flex-end"
                    }
                }}
                PaperProps={{
                    sx: {
                        m: 0,
                        top: 0,
                        right: 0,
                        bottom: 0,
                        minHeight: '100vh'
                    }
                }}
            >
                <DialogTitle>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar sx={{ bgcolor: blue[500] }}><Attachment /></Avatar>
                        <Typography variant="subtitle1" sx={{ color: blue[800] }}>
                            View Uploaded File (format: {fileExtension})...
                        </Typography>
                        <Button variant="outlined" size="small" startIcon={<RotateRight />} onClick={rotateUploadedFile} >Rotation</Button>
                        <FormControl sx={{ m: 1, minWidth: 80 }} size="small">
                            <InputLabel>Resize</InputLabel>
                            <Select
                                label="File Size"
                                value={fileSize}
                                onChange={(event) => resizeUploadedFile(event)}
                            >
                                <MenuItem value={100}>100%</MenuItem>
                                <MenuItem value={75}>75%</MenuItem>
                                <MenuItem value={60}>60%</MenuItem>
                                <MenuItem value={50}>50%</MenuItem>
                                <MenuItem value={25}>25%</MenuItem>
                                <MenuItem value={20}>20%</MenuItem>
                                <MenuItem value={15}>15%</MenuItem>
                                <MenuItem value={5}>5%</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>
                </DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={closeFileViewerDialog}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <Close />
                </IconButton>
                <DialogContent dividers>
                    <Grid container>
                        <Stack direction="row" spacing={1}>
                            <Button variant="outlined" size="small" onClick={convertToPdf} >Convert to PDF</Button>
                            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                                <InputLabel>Time Stamp Size</InputLabel>
                                <Select
                                    label="Time Stamp Size"
                                    value={timeStampSize}
                                    onChange={(event) => updateTimeStampSize(event)}
                                >
                                    <MenuItem value={24}>60%</MenuItem>
                                    <MenuItem value={40}>100%</MenuItem>
                                    <MenuItem value={36}>90%</MenuItem>
                                    <MenuItem value={32}>80%</MenuItem>
                                    <MenuItem value={28}>70%</MenuItem>
                                    <MenuItem value={20}>50%</MenuItem>
                                    <MenuItem value={16}>40%</MenuItem>
                                    <MenuItem value={12}>30%</MenuItem>
                                    <MenuItem value={8}>20%</MenuItem>
                                    <MenuItem value={4}>10%</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField
                                label="Edit Time"
                                size="small"
                                value={timeStampTime}
                                onChange={(event) => { setTimeStampTime(event.target.value) }}
                            />
                            <Button variant="outlined" size="small" onClick={addTimeStamp} >Add Time Stamp</Button>
                            <Button variant="outlined" color="error" size="small" onClick={removeTimeStamp}>Remove Time Stamp</Button>
                        </Stack>
                    </Grid>
                    <Divider light sx={{ my: 1.5 }} />
                    <Grid container sx={{ marginTop: 2 }}>
                        <Grid item xs={1}></Grid>       
                        <Grid item xs={10}>
                            <Paper>
                                <div>
                                    <iframe key={iframeCount} src={fileUrl} width="100%" height="800px" />
                                </div>
                            </Paper>
                        </Grid>
                        <Grid item xs={1}></Grid>       
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={saveProcessedFile} variant="contained" color="success">Save PDF</Button>
                    <Button onClick={closeFileViewerDialog} variant="contained" color="warning">Cancel</Button>
                    <Button onClick={revertToOriginal} variant="contained" color="error">Revert Changes</Button>
                </DialogActions>
            </Dialog>
            {
                alertInfo &&
                <AlertInfoDialog isAlertInfoDialogOpen={isAlertInfoDialogOpen} alertInfo={alertInfo} closeAlertInfoDialog={closeAlertInfoDialog}></AlertInfoDialog>
            }
        </>
    )

}

export default UploadedFileViewer